/**
 * Duva API client — thin wrapper around fetch with JWT auth injection.
 */

// Must match the Worker that issues JWTs (see apps/worker/wrangler.toml APP_URL).
// Override with VITE_API_URL when using a custom API domain (e.g. worker.duva.com).
const BASE_URL = import.meta.env.VITE_API_URL ?? 'https://duva-worker.duva.workers.dev'

const DEFAULT_UPGRADE_URL =
  (import.meta.env.VITE_DASHBOARD_URL as string | undefined) ?? 'https://duva-5xu.pages.dev/settings'

/** Map worker freemium 403 bodies to an upgrade URL for UpgradeRequiredError. */
function freemiumUpgradeUrl(message: string): string | null {
  const billingHint =
    /Upgrade (to|for|at)\b|plan is limited|limited to \d+ seats|reports per month|plan or higher|requires .{0,48} plan or higher/i.test(
      message
    )
  const upgradeInUrl = message.match(/https?:\/\/[^\s]+upgrade[^\s]*/)
  if (upgradeInUrl) return upgradeInUrl[0]
  if (billingHint) {
    const anyHttps = message.match(/https?:\/\/[^\s]+/)
    if (anyHttps) return anyHttps[0]
    return DEFAULT_UPGRADE_URL
  }
  return null
}

// Thrown when API returns 403 with a freemium upgrade gate
export class UpgradeRequiredError extends Error {
  constructor(public override message: string, public upgradeUrl: string) {
    super(message)
    this.name = 'UpgradeRequiredError'
  }
}

const TOKEN_KEY = 'duva_token'
const REFRESH_KEY = 'duva_refresh_token'

function getToken(): string | null {
  return localStorage.getItem(TOKEN_KEY)
}

function getRefreshToken(): string | null {
  return localStorage.getItem(REFRESH_KEY)
}

export function setToken(token: string) {
  localStorage.setItem(TOKEN_KEY, token)
}

export function setRefreshToken(refreshToken: string) {
  localStorage.setItem(REFRESH_KEY, refreshToken)
}

export function clearToken() {
  localStorage.removeItem(TOKEN_KEY)
  localStorage.removeItem(REFRESH_KEY)
}

async function refreshAccessToken(): Promise<boolean> {
  const refreshToken = getRefreshToken()
  if (!refreshToken) return false
  try {
    const res = await fetch(`${BASE_URL}/api/auth/refresh`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ refresh_token: refreshToken }),
    })
    if (!res.ok) return false
    const data = (await res.json()) as { access_token: string; refresh_token: string }
    setToken(data.access_token)
    setRefreshToken(data.refresh_token)
    return true
  } catch {
    return false
  }
}

async function request<T>(path: string, options: RequestInit = {}): Promise<T> {
  let token = getToken()
  const headers: Record<string, string> = {
    'Content-Type': 'application/json',
    ...(options.headers as Record<string, string> ?? {}),
  }
  if (token) headers['Authorization'] = `Bearer ${token}`

  const cache: RequestCache = token ? 'no-store' : 'default'
  let res = await fetch(`${BASE_URL}${path}`, { ...options, headers, cache })

  if (res.status === 401) {
    const refreshed = await refreshAccessToken()
    if (refreshed) {
      token = getToken()
      if (token) headers['Authorization'] = `Bearer ${token}`
      res = await fetch(`${BASE_URL}${path}`, {
        ...options,
        headers,
        cache: getToken() ? 'no-store' : 'default',
      })
    }
    if (res.status === 401) {
      clearToken()
      window.location.href = '/login'
      throw new Error('Unauthorized')
    }
  }

  if (res.status === 403) {
    const err = await res.json().catch(() => ({ error: res.statusText }))
    const msg = (err as Record<string, string>).error ?? 'Access denied'
    const upgradeUrl = freemiumUpgradeUrl(msg)
    if (upgradeUrl) throw new UpgradeRequiredError(msg, upgradeUrl)
    throw new Error(msg)
  }

  if (!res.ok) {
    const err = await res.json().catch(() => ({ error: res.statusText }))
    throw new Error((err as Record<string, string>).error ?? 'API error')
  }

  return res.json()
}

export interface PricingUiConfig {
  visiblePlanNames?: string[]
  highlightPlanName?: string | null
  promoBadge?: string | null
}

export interface Geopricing {
  country: string
  countryName: string
  region: string
  plans: {
    name: string
    monthlyPrice: number
    annualPrice: number
    annualTotal: number
    currency: string
    symbol: string
    userLimit: number | null
    highlight?: boolean
    features: string[]
  }[]
  billingNote: string
  pppNote: string
  promoBadge?: string | null
  pricingUi?: PricingUiConfig | null
}

// ── API methods ────────────────────────────────────────────────────────────────

export const api = {
  // Auth
  getSlackInstallUrl: () => `${BASE_URL}/api/auth/slack/install`,

  // Me
  getMe: () => request<{ id: string; name: string; email: string; role: string; org_id: string }>('/api/me'),
  getGoal: () => request<{ goal: string; track: string; updatedAt: string } | null>('/api/me/goal'),
  postGoal: (goal: string) => request<{ goal: string; track: string; updatedAt: string }>('/api/me/goal', {
    method: 'POST',
    body: JSON.stringify({ goal })
  }),
  getNudgeProgress: () =>
    request<{
      streakDays: number
      completedThisWeek: number
      completedThisMonth: number
      snoozedThisWeek: number
      sentThisWeek: number
    }>('/api/me/nudge-progress'),

  // Analytics
  getEngagement: (days = 30) => request<{ scores: unknown[] }>(`/api/analytics/engagement?days=${days}`),
  getNudgeStats: (days = 30) => request<{ stats: unknown[] }>(`/api/analytics/nudges?days=${days}`),
  getSummary: () => request<{
    engagementScore: { score: number; computed_at: string } | null;
    riskFlags: string[];
    frictionScore: number;
    weeklyStats: { sent: number; opened: number; acted: number } | null;
    activeMemberCount: number;
    isAnonymized: boolean;
  }>('/api/analytics/summary'),
  getMabStats: () => request<{ stats: { category: string; success_count: number; total_count: number; conversion_rate: number }[] }>('/api/analytics/mab'),
  getInsights: () => request<{ insight: string; generatedAt: string }>('/api/analytics/insights'),

  // Nudges
  getNudges: (params?: Record<string, string>) => {
    const qs = params ? '?' + new URLSearchParams(params).toString() : ''
    return request<{ nudges: unknown[]; total: number }>(`/api/nudges${qs}`)
  },
  sendNudge: (body: { userIds: string[]; nudgeId?: string; idempotencyKey: string }) =>
    request('/api/nudges/send', { method: 'POST', body: JSON.stringify(body) }),
  updateDelivery: (deliveryId: string, body: { outcome: string }) =>
    request(`/api/nudges/${deliveryId}`, { method: 'PATCH', body: JSON.stringify(body) }),

  // Org
  getOrgSettings: () => request<{ id: string; name: string; plan: string; employee_count: number }>('/api/org/settings'),
  updateOrgSettings: (body: { name?: string; plan?: string; industry?: string | null }) =>
    request('/api/org/settings', { method: 'PATCH', body: JSON.stringify(body) }),
  upgradePlan: (plan: string) =>
    request('/api/org/settings', { method: 'PATCH', body: JSON.stringify({ plan }) }),
  getMembers: () => request<{ members: unknown[] }>('/api/org/members'),
  updateMember: (userId: string, body: { nudgeTrack?: string | null; nudgeFrequency?: string; preferredChannel?: string; role?: string }) =>
    request(`/api/org/members/${userId}`, { method: 'PATCH', body: JSON.stringify(body) }),
  syncMembers: () => request('/api/org/members/sync', { method: 'POST' }),

  // Reports
  generateReport: () => request<{ reportId: string; shareUrl: string }>('/api/reports/share', { method: 'POST' }),

  // Accountability
  getAccountabilityPairs: () =>
    request<{ pairs: AccountabilityPair[] }>('/api/accountability/pairs'),
  runAccountabilityMatching: () =>
    request<{ pairsCreated: number; pairs: AccountabilityPair[] }>('/api/accountability/pairs/run', { method: 'POST' }),
  deactivatePair: (pairId: string) =>
    request(`/api/accountability/pairs/${pairId}`, { method: 'DELETE' }),

  // Invites
  getInvites: () => request<{ invites: Invite[] }>('/api/invites'),
  sendInvites: (emails: string[], role?: string) =>
    request<{ results: { email: string; status: string }[] }>('/api/invites', {
      method: 'POST',
      body: JSON.stringify({ emails, role }),
    }),
  revokeInvite: (id: string) => request(`/api/invites/${id}`, { method: 'DELETE' }),

  // SSO
  getSsoStatus: () =>
    request<{ configured: boolean; provider: string | null; domain: string | null }>('/api/sso/status'),
  configureSso: (body: { provider: string; issuer_url: string; client_id: string; client_secret: string; domain: string }) =>
    request('/api/sso/configure', { method: 'POST', body: JSON.stringify(body) }),
  getSsoLoginUrl: (domain: string) =>
    `${BASE_URL}/api/sso/login?domain=${encodeURIComponent(domain)}`,

  // Pricing
  getGeoPricing: () => request<Geopricing>('/api/pricing/geo'),
}

const PLATFORM_TOKEN_KEY = 'duva_platform_admin_token'

export function getPlatformAdminToken(): string | null {
  return sessionStorage.getItem(PLATFORM_TOKEN_KEY)
}

export function setPlatformAdminToken(token: string) {
  sessionStorage.setItem(PLATFORM_TOKEN_KEY, token)
}

export function clearPlatformAdminToken() {
  sessionStorage.removeItem(PLATFORM_TOKEN_KEY)
}

async function platformRequest<T>(path: string, options: RequestInit = {}): Promise<T> {
  const token = getPlatformAdminToken()
  if (!token) throw new Error('Missing platform admin token')
  const res = await fetch(`${BASE_URL}${path}`, {
    ...options,
    headers: {
      'Content-Type': 'application/json',
      Authorization: `Bearer ${token}`,
      ...(options.headers as Record<string, string> ?? {}),
    },
    cache: 'no-store',
  })
  if (!res.ok) {
    const err = await res.json().catch(() => ({ error: res.statusText }))
    throw new Error((err as Record<string, string>).error ?? `API error ${res.status}`)
  }
  return res.json()
}

export const platformAdminApi = {
  listWorkspaces: (q?: { limit?: number; offset?: number }) => {
    const params = new URLSearchParams()
    if (q?.limit != null) params.set('limit', String(q.limit))
    if (q?.offset != null) params.set('offset', String(q.offset))
    const qs = params.toString() ? `?${params}` : ''
    return platformRequest<{ workspaces: Record<string, unknown>[]; total: number; limit: number; offset: number }>(
      `/api/platform/workspaces${qs}`
    )
  },
  getWorkspace: (orgId: string) =>
    platformRequest<{ workspace: Record<string, unknown>; members: Record<string, unknown>[] }>(
      `/api/platform/workspaces/${orgId}`
    ),
  patchWorkspace: (orgId: string, body: { plan?: string; industry?: string | null }) =>
    platformRequest<{ workspace: Record<string, unknown> }>(`/api/platform/workspaces/${orgId}`, {
      method: 'PATCH',
      body: JSON.stringify(body),
    }),
  getPricingUi: () => platformRequest<{ pricingUi: PricingUiConfig | null }>('/api/platform/settings/pricing-ui'),
  patchPricingUi: (body: PricingUiConfig) =>
    platformRequest<{ pricingUi: PricingUiConfig }>('/api/platform/settings/pricing-ui', {
      method: 'PATCH',
      body: JSON.stringify(body),
    }),
  getAudit: (limit = 50) =>
    platformRequest<{ entries: Record<string, unknown>[] }>(`/api/platform/audit?limit=${limit}`),
  getNudgeStats: () =>
    platformRequest<{
      byStatus: { status: string; count: number }[]
      approvedByCategory: { category: string; count: number }[]
      totalApproved: number
      totalAll: number
    }>('/api/platform/nudges/stats'),
  listPlatformNudges: (status?: string) => {
    const q = status ? `?status=${encodeURIComponent(status)}` : ''
    return platformRequest<{ nudges: Record<string, unknown>[] }>(`/api/platform/nudges${q}`)
  },
  patchPlatformNudge: (id: string, body: { status?: string; review_notes?: string | null }) =>
    platformRequest<{ nudge: Record<string, unknown> }>(`/api/platform/nudges/${id}`, {
      method: 'PATCH',
      body: JSON.stringify(body),
    }),
}

// ── Shared types (web-side) ────────────────────────────────────────────────────
export interface Invite {
  id: string
  email: string
  role: string
  created_at: string
  expires_at: string
  invited_by_name: string
}

export interface AccountabilityPair {
  id: string
  nudge_track: string
  paired_at: string
  user_a_id: string
  user_a_name: string
  user_a_email: string
  user_a_slack_id?: string | null
  user_a_teams_id?: string | null
  user_b_id: string
  user_b_name: string
  user_b_email: string
  user_b_slack_id?: string | null
  user_b_teams_id?: string | null
}
